import Vue from 'vue';
import VueRouter from 'vue-router';
import Home from '@/views/auth/Home.vue';
import Login from '@/views/guest/Login.vue';
import Error404 from '@/views/Error404.vue';
import ResetPw from '@/views/guest/ResetPw.vue';
import SetNewPassword from '@/views/guest/SetNewPassword.vue';
import { getStore } from '@/store/index';

import routesEvent from './event';
import routesAddresses from './addresses';
import routesMagazin from './magazin';
import routesService from './service';
import routesBanner from './banner';
import routesAdmin from './admin';
import routesUser from './user';
import routesRequestedUser from './requested_user';
import routesMediamanager from './media_manager';
import { setBrowserTitle } from '@/helpers/tools';
import { getI18nInstance } from '@/i18n';

const CmpShowcase = () => import(/* webpackChunkName: "dev" */ '@/views/CmpShowcase.vue');

Vue.use(VueRouter);

const authRoutes = [
    {
        path: '/',
        name: 'home',
        component: Home,
        meta: {
            requiresAuth: true,
            title: () => getI18nInstance().t('dashboard'),
        },
    },
    ...routesAddresses,
    ...routesBanner,
    ...routesEvent,
    ...routesMagazin,
    ...routesService,
    ...routesAdmin,
    ...routesUser,
    ...routesRequestedUser,
    ...routesMediamanager,
];

const routes = [
    ...authRoutes,
    {
        path: '/login',
        name: 'login',
        component: Login,
    },
    {
        path: '/cmp-showcase',
        name: 'cmp-showcase',
        component: CmpShowcase,
    },
    {
        path: '/reset_pw',
        name: 'reset_pw',
        component: ResetPw,
    },
    {
        // target of the "Passwort vergessen" mail link, needs ?token=...
        path: '/set_new_password',
        name: 'set_new_password',
        component: SetNewPassword,
    },
    {
        // catch-all, or 404 not found:
        path: '*',
        name: 'Error404',
        component: Error404,
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
});

router.beforeEach((to, from, next) => {
    // Auth requiring routes need an auth user:
    if (to.matched.some((rec) => rec.meta.requiresAuth)) {
        if (!getStore().getters.isAuthenticated) {
            return next({
                name: 'login',
                query: {
                    redirect: encodeURIComponent(window.location.href),
                },
            });
        } else {
            // Check if the route needs specific privileges
            const user = getStore().getters.user;

            let allRoutesAllowed =
                user.isAdmin ||
                to.matched.every((routeRec) => {
                    if (routeRec.meta.roles && routeRec.meta.roles.length > 0) {
                        for (let neededRole of routeRec.meta.roles) {
                            if (user.role === neededRole) {
                                return true;
                            }
                        }
                        // no role matches
                        return false;
                    } else {
                        // no roles needed
                        return true;
                    }
                });
            if (allRoutesAllowed) {
                return next();
            } else {
                // no access, abort
                return next(false);
            }
        }
    }

    // guest routes can only be accessed when not auth:
    if (to.matched.some((rec) => rec.meta.guest)) {
        if (getStore().getters.isAuthenticated) {
            return next({
                name: 'home',
            });
        } else {
            return next();
        }
    }

    // If you come this far, the route can be displayed in either case:
    return next();
});

router.afterEach((to, from) => {
    // see https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609 why we use
    // nextTick here:
    Vue.nextTick(() => {
        // Update the page's title, based on the Route's meta data, if applicable:
        let title = (to.meta && to.meta.title) || null;
        if (title instanceof Function) {
            title = title(to, from);
        }

        // after the route has changed, we check if the Component provides a
        // 'getBrowserTitle' function. If yes, we call it, and use whatever is returned from it as title:
        for (const match of to.matched) {
            let cmp = match.instances?.default;
            if (typeof cmp?.getBrowserTitle === 'function') {
                title = cmp.getBrowserTitle();
                break;
            }
        }
        // finally, we set the browser's title after routing:
        setBrowserTitle(title);
    });
});

export default router;
